import { roleAtLeast, type MeResponse, type MembershipSummary, type Project, type Role, type Workspace } from '@lia/contracts';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useSearchParams } from 'react-router';
import { api, ApiError } from '../lib/api.js';
import { useApiQuery } from '../lib/hooks.js';
import { qk, wk } from '../lib/query.js';

export function useMe() {
  return useQuery<MeResponse, ApiError>({
    queryKey: qk.me,
    queryFn: () => api('me'),
    staleTime: 60_000,
    retry: (count, err) => !(err instanceof ApiError && ['unauthorized', 'forbidden'].includes(err.kind)) && count < 2,
  });
}

export interface WorkspaceContextValue {
  workspaceId: string;
  workspace: Workspace;
  membership: MembershipSummary;
  me: MeResponse;
  role: Role;
  /** Workspace IANA timezone: pass it to every date component. */
  timezone: string;
  can: (required: Role) => boolean;
  isAdmin: boolean;
}

const WorkspaceContext = React.createContext<WorkspaceContextValue | null>(null);
export const WorkspaceProvider = WorkspaceContext.Provider;

export function useWorkspace(): WorkspaceContextValue {
  const ctx = React.useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace debe usarse dentro de una ruta de espacio de trabajo');
  return ctx;
}

/** Optional variant for components rendered both inside and outside a workspace (e.g. the security form). */
export function useOptionalWorkspace(): WorkspaceContextValue | null {
  return React.useContext(WorkspaceContext);
}

export function makeCan(role: Role) {
  return (required: Role) => roleAtLeast(role, required);
}

/** Project filter persisted in the URL (`?proyecto=`). null = all permitted projects. */
export function useProjectFilter(): [string | null, (projectId: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get('proyecto');
  const set = React.useCallback(
    (projectId: string | null) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (projectId) next.set('proyecto', projectId);
          else next.delete('proyecto');
          next.delete('cursor');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  return [value, set];
}

/** Projects the current user can access in the workspace (cached; shared by header filter and forms). */
export function useProjects() {
  const { workspaceId } = useWorkspace();
  return useApiQuery(wk.projects(workspaceId), 'listProjects', { params: { workspaceId } }, { staleTime: 60_000 });
}

export function projectName(projects: readonly Project[] | undefined, id: string | null | undefined): string | null {
  if (!id) return null;
  return projects?.find((p) => p.id === id)?.name ?? null;
}

const LAST_WS_KEY = 'lia:last-workspace';

/** Remembers the last visited workspace id (not a secret) for the platform screen's back link. */
export function rememberWorkspace(id: string) {
  try {
    sessionStorage.setItem(LAST_WS_KEY, id);
  } catch {
    // storage unavailable
  }
}

export function lastWorkspace(memberships: readonly MembershipSummary[]): MembershipSummary | null {
  let id: string | null = null;
  try {
    id = sessionStorage.getItem(LAST_WS_KEY);
  } catch {
    id = null;
  }
  return memberships.find((m) => m.workspaceId === id) ?? memberships[0] ?? null;
}
